import { DEFAULT_INDEX_GROUP } from '@/constants/constants';
import type { SymbolKey } from '@/entities/symbol';
import type { ExportRequestBody } from '@/features/DataHub/api/export.api';
import type { ExportRequestParams } from '@/features/DataHub/modules/Export/types';

import { transformSymbols } from '../utils';

export type ExportRequestBaseParams = {
  gxURL: string;
  symbols: SymbolKey[];
  fromValue: string;
  toValue: string | null;
  includeMetadata: boolean;
  userTimezone: string;
  apiKey?: string;
};

export type ExportCurlRequestParams = ExportRequestBaseParams & {
  apiKey: string;
};

export interface BuildRequestBodyParams {
  id?: string;
  name: string | null;
  exportType: string;
  exportSubType: string | null;
  from?: string | null;
  to?: string | null;
  timezone?: string;
  dateFormat?: string;
  includeMetadata?: boolean;
  symbols?: SymbolKey[];
  group?: string;
}

export type ExportEndpointParams = {
  type: string;
  fromValue: string;
  toValue: string | null;
  includeMetadata: boolean;
  timezone: string;
  dateFormat: string;
};

const uniqueJoined = (values: string[]) => [...new Set(values)].join(',');

export function buildExportApiRequestString(params: ExportRequestBaseParams): string {
  const { gxURL, symbols, fromValue, toValue, includeMetadata, userTimezone, apiKey } = params;

  const codes = uniqueJoined(symbols.map(s => s.Code));
  const periods = uniqueJoined(symbols.map(s => s.Period));
  const periodTypes = uniqueJoined(symbols.map(s => s.PeriodType));
  const timeRefs = uniqueJoined(symbols.map(s => s.TimeRef));

  const urlParams = new URLSearchParams();
  urlParams.set('code', codes);
  if (periods.length > 0) urlParams.set('period', periods);
  if (periodTypes.length > 0) urlParams.set('periodType', periodTypes);
  if (timeRefs.length > 0) urlParams.set('timeRef', timeRefs);
  urlParams.set('from', fromValue);
  if (toValue) urlParams.set('to', toValue);
  urlParams.set('metadata', String(includeMetadata));
  urlParams.set('timezone', userTimezone);
  if (apiKey) urlParams.set('token', apiKey);

  return `${gxURL}/index?${urlParams.toString()}`;
}

export function buildExportCurlRequestString(params: ExportCurlRequestParams): string {
  const { apiKey, ...baseParams } = params;
  const url = buildExportApiRequestString(baseParams);
  return `curl --location "${url}" --header "Accept: application/json" --header "Authorization: ApiKey ${apiKey}"`;
}

export function buildExportEndpoint(params: ExportEndpointParams): string {
  const { type, fromValue, toValue, includeMetadata, timezone, dateFormat } = params;
  const urlType = type.startsWith('excel') ? type : `third-party?type=${type}`;
  const toParam = toValue ? `&to=${toValue}` : '';
  return `export/${urlType}&from=${fromValue}${toParam}&metadata=${includeMetadata}&timezone=${timezone}&dateFormat=${dateFormat}`;
}

export function buildExportBody(symbols: SymbolKey[], group: string): ExportRequestBody {
  return {
    symbols: symbols.map(symbol => ({
      group,
      key: {
        code: symbol.Code,
        periodType: symbol.PeriodType,
        timeRef: symbol.TimeRef,
        period: symbol.Period
      }
    }))
  };
}

export function buildExportRequestBody(params: BuildRequestBodyParams): ExportRequestParams {
  const {
    id,
    name,
    exportType,
    exportSubType,
    from,
    to,
    timezone,
    dateFormat,
    includeMetadata,
    symbols,
    group
  } = params;

  const { symbols: transformedSymbols } = transformSymbols(
    symbols ?? [],
    group ?? DEFAULT_INDEX_GROUP
  );

  return {
    ...(id && { id }),
    name,
    exportType,
    exportSubType,
    from: from ?? null,
    to: to ?? null,
    timezone,
    dateFormat,
    includeMetadata,
    symbols: transformedSymbols
  };
}
